import moment from "moment";
import router from "@/components/common/Router";
import * as yup from "yup";

import { store } from "@/configureStore";
import { API_PATH } from "@/constants";
import dataFetcher from "@/modules/dataFetcher";
import { createFormModule } from "@/modules/forms";
import { createModalModule } from "@/modules/modal";
import api from "@/utils/api";

import { getLocationOptions } from "./LocationSelector";
import { createSelector } from "reselect";
import { API_URL } from "@/constants";
import { get, set, debounce } from "lodash";

//

export const appointmentConfirmation = createModalModule();
export const appointmentReview = createModalModule();

const DEFAULT_SERVICE = {
  guarantee: 0,
  price: 0,
  device: 4,
  brand: 20,
  model: 748,
  status: -1,
  reparation: 54,
};

const requiredAddress = yup.string().when("location", {
  is: location => location === 'home' || location === 'pickup',
  then: yup.string().required(),
});

// NOTE: when adding address validation
// use the when method (https://github.com/jquense/yup#mixedwhenkeys-string--arraystring-builder-object--value-schema-schema-schema)
const validator = yup.object({
  name: yup.string().required(),
  email: yup.string().required().email(),
  tel: yup.string().required(),
  time: yup.string().when(["type", "location"], {
    is(type, location) {
      return type === "contact" || location === "home" || location === "pickup";
    },
    otherwise: yup.string().required(),
  }),
  enquiry: yup.string().when("type", {
    is: "contact",
    then: yup.string().required(),
  }),
  address: requiredAddress,
  city: requiredAddress,
  zip: requiredAddress,
});

export const appointmentForm = createFormModule({
  validator,
  async init({ shop, type = "appointment" }) {
    const fromAddressBar = router.router.query;

    const locations = getLocationOptions(shop).filter(
      (option) => !option.disabled
    );

    function getDefaultValue(type, defaultValue) {
      const value = fromAddressBar[type];
      if (["undefined", "null"].includes(value)) {
        return defaultValue;
      }

      return value;
    }

    const address = [shop.street || "", shop.city || ""]
      .filter(Boolean)
      .join(", ");
    const params = appointmentData.getParams();
    const values = {
      shop: shop.id,
      shopAddress: address,
      shopName: shop.name,
      ...fromAddressBar,
      device: params?.device,
      brand: params?.brand,
      model: params?.model,
      service: params?.reparation,
      color: params?.color,
      location: locations?.[0]?.value || "in-store",
      paymentType: "cash",
      type,
      time: "",
      name: "",
      email: "",
      tel: "",
      address: "",
      city: "",
      zip: "",
      state: "",
      date: new Date().toString(),
    };

    return values;
  },

  async submit(data) {
    const service = serviceFetcher.selector(store.ref.getState()).result;
    const formatedDate = moment(data.date).format("YYYY-MM-DD");
    const reparationId = service?.reparation.id
      ? parseInt(service.reparation.id)
      : 0;

    let repairSeviceData = {
      device: parseInt(data.device),
      brand: parseInt(data.brand),
      model: parseInt(data.model),
      color: parseInt(data.color),
      status: -1,
      price: service?.price,
      guarantee: service?.guarantee_time,
      reparation: reparationId,
    };
    let emailServiceData = {
      appointment: 389,
      device: parseInt(data.device),
      brand: parseInt(data.brand),
      model: parseInt(data.model),
      price: service?.price,
      guarantee: service?.guarantee_time,
      reparation: reparationId,
    };

    if (!reparationId) {
      repairSeviceData = {
        guarantee: 0,
        price: 0,
        device: 4,
        brand: 20,
        model: 748,
        status: -1,
        reparation: 54,
      };
      emailServiceData = {
        appointment: 389,
        device: 1,
        brand: 1,
        model: 1,
        price: 25,
        guarantee: 12,
        reparation: 2,
      };
    }

    let client_address;
    if (data.location !== "in-store") {
      client_address = [data.address, data.city, data.state, data.zip]
        .filter(Boolean)
        .join(", ");
    }

    const payload = {
      name: data.shopName,
      address: data.shopAddress,
      datetime: data.type === "contact" ? "" : `${formatedDate} - ${data.time}`,
      appointmentData: {
        date: data.type === "contact" ? "" : formatedDate,
        time: data.type === "contact" ? "" : data.time,
        appointment_type:
          data.type === "contact" ? 3 : data.location === "in-store" ? 1 : 2,
        reparation: reparationId || 54,
        client_name: data.name,
        client_email: data.email,
        client_phone: data.tel,
        shop: data.shop,
        appointment_comment: data.enquiry,
        client_address,
        active: 1,
      },
      repairSeviceData,
    };

    const appointment = await api.post(
      `${API_PATH.CREATEAPPOINTMENT}/`,
      payload
    );
    if (repairSeviceData?.model == -1) {
      repairSeviceData.model = null
    }
    const create_repair_service = await api.post(
      `${API_PATH.CREATESHOPREPAIRSERVICE}/`,
      {
        appointment: appointment.appointment_id,
        ...repairSeviceData,
        status: undefined,
      }
    );

    emailServiceData.appointment = appointment.appointment_id;
    api.post(`${API_PATH.APPOINTMENTEMAIL}/`, emailServiceData);

    return create_repair_service;
  },
});

export const deviceFetcher = dataFetcher({
  selectors: [
    "appoitment",
    "device",
    () => appointmentData.getParams()?.shop?.id,
    () => appointmentData.getParams()?.device,
  ],
  async fetchData([_1, _2, shopId, deviceId]) {
    const data = await api.get(`${API_PATH.GETSHOPDEVICES}/`, { shop: shopId });

    return data
      .map(({ device }) => device)
      .find((device) => `${device.id}` === deviceId);
  },
});

export const brandFetcher = dataFetcher({
  selectors: [
    "appoitment",
    "brand",
    () => appointmentData.getParams()?.shop?.id,
    () => appointmentData.getParams()?.device,
    () => appointmentData.getParams()?.brand,
  ],
  async fetchData([_1, _2, shop, deviceId, brandId]) {
    console.log(shop)
    const data = await api.get(`${API_PATH.GETDEVICEBRANDS}/?`, {
      device: deviceId,
      shop,
    });

    return data
      .map(({ brand }) => brand)
      .find((brand) => `${brand.id}` === brandId);
  },
});

export const modelFetcher = dataFetcher({
  selectors: [
    "appoitment",
    "model",
    () => appointmentData.getParams()?.shop?.id,
    () => appointmentData.getParams()?.device,
    () => appointmentData.getParams()?.brand,
    () => appointmentData.getParams()?.model,
  ],
  async fetchData([_1, _2, shop, deviceId, brandId, modelId]) {
    const data = await api.get(`${API_PATH.GETBRANDMODELS}/`, {
      device: deviceId,
      shop,
      brand: brandId,
    });
    return data
      .map(({ model }) => model)
      .find((model) => `${model.id}` === modelId);
  },
});

export const serviceFetcher = dataFetcher({
  selectors: [
    "appoitment",
    "service",
    () => appointmentData.getParams()?.shop?.id,
    () => appointmentData.getParams()?.device,
    () => appointmentData.getParams()?.brand,
    () => appointmentData.getParams()?.model,
    () => appointmentData.getParams()?.reparation,
  ],
  async fetchData([_1, _2, shop, deviceId, brandId, modelId, serviceId]) {
    const data = await api.get(`${API_PATH.GETSHOPREPARATIONDETAILS}/`, {
      device: deviceId,
      shop,
      brand: brandId,
      model: modelId || -1,
    });
    return data.find((service) => `${service.id}` === serviceId);
  },
});

export const openTimeFetcher = dataFetcher({
  selectors: [
    "appoitment",
    "open-time",
    () => appointmentForm.state.values.shop,
  ],
  async fetchData([_1, _2, shop]) {
    const response = await api.get(`${API_PATH.GETVALIDOPENTIME}/${shop}/`);
    try {
      return JSON.parse(response?.[0]?.valid_day_time || "");
    } catch (err) {
      return null;
    }
  },
});

export const invalidTimeFetcher = dataFetcher({
  selectors: [
    "appoitment",
    "invalid-time",
    () => appointmentForm.state?.values.shop,
  ],
  async fetchData([_1, _2, shop]) {
    const data = await api.get(`${API_PATH.GETINVALIDOPENTIME}/${shop}/`);
    return JSON.parse(data?.[0]?.invalid_day_time || "[]");
  },
});

export function payForAppointment({ appointment, shop, service }) {
  return api.post(`${API_PATH.PAYMENT}`, {
    appointment,
    shop: shop.id,
    price: `${service.price}.00`,
    title: `${shop.name} appointment payment`,
  });
}

export const appointmentData = {
  paramsSelector: createSelector(
    state => state.appointment?.params,
    (params) => {
      return params || {}
    }
  ),
  updateParams: (params) => {
    const existingParams = appointmentData.getParams()
    const newParams = { ...existingParams, ...params }
    // console.log('new params update')
    // console.log(newParams)
    store.ref.dispatch({
      type: "SET_APPOINTMENT_PARAMS",
      payload: newParams,
    });
  },

  get state() {
    return store.ref.getState().appointment;
  },
  getParams: () => {
    return store?.ref?.getState()?.appointment?.params;
  },

  getDevice: () => {
    const params = appointmentData.getParams() || {}
    const shopId = params.shop?.id
    const deviceId = params.device
    const guid = `appoitment~device~${shopId}~${deviceId}`
    // console.log(guid)
    return store?.ref?.getState()?.fetcher?.[guid]?.result;

  },
  getBrand: () => {
    const params = appointmentData.getParams() || {}
    const shopId = params.shop?.id
    const deviceId = params.device
    const brandId = params.brand
    const guid = `appoitment~brand~${shopId}~${deviceId}~${brandId}`
    // console.log(guid)
    return store?.ref?.getState()?.fetcher?.[guid]?.result;

  },
  getModel: () => {
    const params = appointmentData.getParams() || {}
    const shopId = params.shop?.id
    const deviceId = params.device
    const brandId = params.brand
    const modelId = params.model
    const guid = `appoitment~model~${shopId}~${deviceId}~${brandId}~${modelId}`
    // console.log(guid)
    return store?.ref?.getState()?.fetcher?.[guid]?.result;

  },
  getRepair: () => {
    const params = appointmentData.getParams() || {}
    const shopId = params.shop?.id
    const deviceId = params.device
    const brandId = params.brand
    const modelId = params.model
    const repairId = params.reparation
    const guid = `appoitment~service~${shopId}~${deviceId}~${brandId}~${modelId}~${repairId}`
    // console.log(guid)
    return store?.ref?.getState()?.fetcher?.[guid]?.result;

  },
  isReparationSelected: () => {
    return appointmentData.getParams()?.reparation
  },
  selector: createSelector(
    (state) => state.appointment,
    (data) => {
      return data?.params;
    }
  ),
  fetchShop: async (shopName) => {
    return await api.get(`${API_URL}/get-shop/?shop=${shopName}`)
  },

  fetchDevices: async () => {
    const params = appointmentData.getParams()
    const shopId = appointmentData.getParams()?.shop?.id
    const devices = await api.get(`${API_URL}/get-shop-devices/?shop=${shopId}`)
    return shopId ? devices : []
  },

  fetchBrands: async () => {
    const params = appointmentData.getParams()
    const shopId = params && params.shop?.id
    const device = shopId && params.device
    if (device) {
      return await api.get(`${API_URL}/get-shop-brands/?shop=${shopId}&device=${device}`)
    }
    return []
  },
  fetchModels: async () => {
    const params = appointmentData.getParams()
    const shopId = params && params.shop?.id
    const device = shopId && params.device
    const brand = shopId && params.brand
    if (brand) {
      return await api.get(`${API_URL}/get-shop-models/?shop=${shopId}&device=${device}&brand=${brand}`)
    }
    return []
  },
  fetchInlineModels: async () => {
    const params = appointmentData.getParams()
    const shopId = params && params.shop?.id
    if (shopId) {
      return await api.get(`${API_URL}/models/?shop=${shopId}`)
    }
    return []
  },
  fetchColors: async () => {
    const colors = await api.get(`${API_URL}/get-colors/`)
    // const colors = [
    //   {id:1, name:"White", ui_code:"#FFFFFF"},
    //   {id:2, name:"Green", ui_code:"#06C987"},
    //   {id:3, name:"Pink", ui_code:"#EF359E"},
    // ]
    return colors
  },
  fetchRepairs: async () => {
    const params = appointmentData.getParams()
    const shopId = params && params.shop?.id
    const device = shopId && params.device
    const brand = shopId && params.brand
    const model = (shopId && params.model) || -1
    if (model) {
      return await api.get(`${API_URL}/get-shop-reparations/?shop=${shopId}&device=${device}&brand=${brand}&model=${model}`)
    }
    return []
  },
  fetchSearchDevices: async () => {
    const devices = await appointmentData.fetchDevices();
    // console.log(devices)
    const devicesModel = devices.map(device => {
      return {
        id: device.device_id,
        name: device.device?.device_name,
        image: device.device?.device_image
      }
    })
    return devicesModel
  },
  fetchSearchModels: async () => {
    const data = await appointmentData.fetchModels();
    const dataModel = data.map(item => {
      return {
        id: item.model_id,
        name: item.model?.model_name,
        image: item.model?.model_photo_url,
        colors: item.model?.colors
      }
    })
    return dataModel
  },
  fetchSearchBrands: async () => {
    const brands = await appointmentData.fetchBrands();
    // console.log(brands)
    const brandsModel = brands.map(brand => {
      return {
        id: brand.brand_id,
        name: brand.brand?.brand_name,
        image: brand.brand?.brand_image
      }
    })
    return brandsModel
  },
  fetchSearchRepairs: async () => {
    const data = await appointmentData.fetchRepairs();
    const dataModel = data.map(item => {
      return {
        id: item.id,
        name: item.reparation?.reparation_name,
        image: item.reparation?.repair_image,
        result: item,
      }
    })
    return dataModel
  }

}
